import { useState } from 'react';
import { AlertTriangle, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { formatPhp } from '@features/quotations/utils/pricing';
import type {
  BeforeAfterDecimal,
  BeforeAfterString,
  PricingDriftDetail,
} from '@features/quotations/types/pricingDrift.types';

/**
 * 86d32t157 Phase 3 — drift surface. When the server returns 409 PRICING_DRIFT
 * because NetSuite master-data changed between save and submit, the panel opens
 * this dialog with the structured before/after. The Refresh button re-saves
 * with the now-current values (the next save reads the cache state that drove
 * the drift) and re-attempts the submit.
 *
 * 86d3mnuxe FIX 5 — drift is a DECISION, not an error: for cutting lists the
 * dialog also offers [Keep quoted pricing] (posts at the snapshot values the
 * customer was already quoted; audited server-side — owner-ruled audit-only).
 * Sales Orders never pass `onKeepQuoted` — the FO is a customer contract, so
 * refresh-at-current is the only offer (owner ruling).
 */
export interface PricingDriftDialogProps {
  detail: PricingDriftDetail;
  open: boolean;
  onClose: () => void;
  /** Called when sales clicks Refresh. The parent re-runs save + submit. */
  onRefreshAndResubmit: () => Promise<void>;
  /** CL-only (86d3mnuxe FIX 5): resubmit honoring the QUOTED (snapshot) pricing.
   *  Omit for SO — the keep-old choice isn't offered on contract-backed pricing. */
  onKeepQuoted?: () => Promise<void>;
  /** Resolve a line's NetSuite item id to its human SKU/description for display. Falls back to the
   *  raw id when unmapped. The drift payload only carries item ids; the parent has the descriptions. */
  resolveItemLabel?: (matchedItemId: string) => string | undefined;
}

export function PricingDriftDialog({
  detail,
  open,
  onClose,
  onRefreshAndResubmit,
  onKeepQuoted,
  resolveItemLabel,
}: PricingDriftDialogProps) {
  // Only price changes are shown per line — bracket drift is a NetSuite-internal tier id, meaningless
  // to sales, so it's excluded from this sales-facing dialog (86d3mnuxe FIX 5 UX). A line whose only
  // change was its bracket therefore contributes no row.
  const pricedLines = detail.details.lineDrift.filter((l) => l.finalUnitPrice);
  const [busyAction, setBusyAction] = useState<'refresh' | 'keep' | null>(null);
  const isRefreshing = busyAction !== null;

  const handleRefresh = async () => {
    setBusyAction('refresh');
    try {
      await onRefreshAndResubmit();
    } finally {
      setBusyAction(null);
    }
  };

  const handleKeepQuoted = async () => {
    if (!onKeepQuoted) return;
    setBusyAction('keep');
    try {
      await onKeepQuoted();
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          {/* 86d3mnuxe funnel polish (owner wording) — present-tense, submission-moment framing:
              post-FIX 5 this dialog can ONLY appear at a submit/retry moment, so "changed during
              submission" is always accurate (the old "since you last saved" read as retrospective). */}
          <DialogTitle className="flex items-center gap-2 text-warning">
            <AlertTriangle className="h-5 w-5" />
            Pricing changed during submission
          </DialogTitle>
          <DialogDescription>
            {onKeepQuoted ? (
              <>
                The NetSuite pricing changed while this quotation was being submitted. Review the
                changes below, then choose: keep the pricing already quoted to the customer, or
                apply the current values and resubmit. Nothing has been posted yet.
              </>
            ) : (
              <>
                The NetSuite pricing changed while this order was being submitted. Review the
                changes below, then apply the current values and resubmit. Nothing has been
                posted yet.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm max-h-[60vh] overflow-y-auto">
          {detail.details.baseMarkup && (
            <Section title="Base markup">
              {Object.entries(detail.details.baseMarkup).map(([grade, ba]) => (
                <Row key={grade} label={`G${grade.replace(/^g/, '')}`}>
                  <BeforeAfterPercent ba={ba} />
                </Row>
              ))}
            </Section>
          )}

          {detail.details.tierClassification && (
            <Section title="Tier classification">
              {detail.details.tierClassification.tier && (
                <Row label="Tier">
                  <BeforeAfterText ba={detail.details.tierClassification.tier} />
                </Row>
              )}
              {detail.details.tierClassification.sizeMix && (
                <Row label="Size mix">
                  <BeforeAfterText ba={detail.details.tierClassification.sizeMix} />
                </Row>
              )}
              {detail.details.tierClassification.adjustmentPct && (
                <Row label="Adjustment">
                  <BeforeAfterPercent ba={detail.details.tierClassification.adjustmentPct} />
                </Row>
              )}
            </Section>
          )}

          {detail.details.freight && (
            <Section title="Freight">
              {detail.details.freight.kindChanged && (
                <Row label="Billing mode">
                  <BeforeAfterText ba={detail.details.freight.kindChanged} />
                </Row>
              )}
              {Object.entries(detail.details.freight.numeric).map(([key, ba]) => (
                <Row key={key} label={fieldLabel(key)}>
                  <BeforeAfterCurrency ba={ba} />
                </Row>
              ))}
            </Section>
          )}

          {pricedLines.length > 0 && (
            <Section title={`Line items (${pricedLines.length})`}>
              {pricedLines.map((line, i) => (
                <div
                  key={line.matchedItemId}
                  className={cn('rounded-sm px-2 py-1.5', i % 2 === 0 && 'bg-muted/40')}
                >
                  <div className="text-sm font-medium text-foreground">
                    {resolveItemLabel?.(line.matchedItemId) ?? line.matchedItemId}
                  </div>
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">Unit price</span>
                    <span><BeforeAfterCurrency ba={line.finalUnitPrice!} /></span>
                  </div>
                </div>
              ))}
            </Section>
          )}

          <div className="border-t pt-3 flex items-baseline justify-between">
            <span className="font-medium">Net change to total</span>
            <span
              className={
                detail.totalDelta > 0
                  ? 'font-semibold tabular-nums text-destructive'
                  : detail.totalDelta < 0
                    ? 'font-semibold tabular-nums text-success'
                    : 'font-semibold tabular-nums'
              }
            >
              {detail.totalDelta > 0 ? '+' : ''}
              {formatPhp(detail.totalDelta)}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isRefreshing}>
            Cancel
          </Button>
          {/* 86d3mnuxe FIX 5 — CL-only: honor the quoted (snapshot) pricing. Audited server-side. */}
          {onKeepQuoted && (
            <Button variant="outline" onClick={() => void handleKeepQuoted()} disabled={isRefreshing}>
              {busyAction === 'keep' ? (
                <>
                  <ShieldCheck className="mr-2 h-4 w-4 animate-pulse" />
                  Submitting at quoted…
                </>
              ) : (
                <>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Keep quoted pricing
                </>
              )}
            </Button>
          )}
          <Button onClick={handleRefresh} disabled={isRefreshing}>
            {busyAction === 'refresh' ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Refreshing & resubmitting…
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                {onKeepQuoted ? 'Use current pricing' : 'Refresh & resubmit'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fieldLabel(key: string): string {
  switch (key) {
    case 'ratePerTon': return 'Rate / ton';
    case 'flatRatePerTruck': return 'Flat rate / truck';
    case 'tollPerTruck': return 'Toll / truck';
    case 'discountPerTon': return 'Discount / ton';
    case 'totalAmount': return 'Total';
    default: return key;
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {/* Flush rows so the alternating shade reads as continuous zebra stripes (86d3mnuxe FIX 5 UX —
          the label and its before→after sit far apart; striping keeps each pair visually together). */}
      <div>{children}</div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2 rounded-sm px-2 py-1 text-sm odd:bg-muted/40">
      <span className="text-muted-foreground">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function BeforeAfterText({ ba }: { ba: BeforeAfterString }) {
  return (
    <span>
      <span className="line-through text-muted-foreground">{ba.snapshot ?? '—'}</span>{' '}
      → <span className="font-medium">{ba.current ?? '—'}</span>
    </span>
  );
}

function BeforeAfterCurrency({ ba }: { ba: BeforeAfterDecimal }) {
  return (
    <span className="tabular-nums">
      <span className="line-through text-muted-foreground">{formatPhp(ba.snapshot)}</span>{' '}
      → <span className="font-medium">{formatPhp(ba.current)}</span>
    </span>
  );
}

function BeforeAfterPercent({ ba }: { ba: BeforeAfterDecimal }) {
  const fmt = (n: number) => `${(n * 100).toFixed(2)}%`;
  return (
    <span className="tabular-nums">
      <span className="line-through text-muted-foreground">{fmt(ba.snapshot)}</span>{' '}
      → <span className="font-medium">{fmt(ba.current)}</span>
    </span>
  );
}
